// Geometría de un trayecto en tiempo real: convierte «metros recorridos según la API» en un
// punto del mapa. Lo normal es el trazado oficial por calles (`fromRoutePath`), que pasa por
// cada parada; `buildRouteGeom` queda para trayectos sin trazado (recorte del plano o recta).

import { clipBetween, haversine, polylineLength, type LatLng } from "../geo";

export interface RouteGeom {
  cum: number[]; // metros API de cada parada
  segs: { pts: LatLng[]; acc: number[]; len: number }[]; // tramo i = parada i → i+1
}

const SNAP_MAX = 60; // m: si la parada está más lejos del trazado, no nos fiamos del trazado

function withAcc(raw: LatLng[]): { pts: LatLng[]; acc: number[]; len: number } {
  // Quitamos puntos repetidos (las paradas suelen caer encima del inicio/fin del recorte): un
  // tramo de longitud cero daría un rumbo sin sentido.
  const pts: LatLng[] = [raw[0]!];
  const acc = [0];
  for (let k = 1; k < raw.length; k++) {
    const step = haversine(pts[pts.length - 1]!, raw[k]!);
    if (step < 0.5 && k < raw.length - 1) continue;
    pts.push(raw[k]!);
    acc.push(acc[acc.length - 1]! + step);
  }
  return { pts, acc, len: acc[acc.length - 1]! };
}

export function buildRouteGeom(
  stopsLL: readonly LatLng[],
  dists: readonly number[],
  linePaths: LatLng[][] | undefined,
): RouteGeom {
  const segs: RouteGeom["segs"] = [];
  for (let i = 0; i + 1 < stopsLL.length; i++) {
    const a = stopsLL[i]!;
    const b = stopsLL[i + 1]!;
    const straight = haversine(a, b);
    const apiLen = Math.max(0, dists[i + 1]! - dists[i]!);
    let pts: LatLng[] = [a, b];
    const clip = linePaths && linePaths.length > 0 ? clipBetween(linePaths, a, b) : null;
    if (clip && clip.length >= 2) {
      const len = polylineLength(clip);
      const snapped = haversine(clip[0]!, a) < SNAP_MAX && haversine(clip[clip.length - 1]!, b) < SNAP_MAX;
      const plausible = len >= straight * 0.9 && len <= Math.max(apiLen * 1.5, straight * 1.2 + 50);
      if (snapped && plausible) pts = [a, ...clip, b];
    }
    segs.push(withAcc(pts));
  }
  return { cum: dists.slice(), segs };
}

const toRad = (d: number): number => (d * Math.PI) / 180;
export function bearing(a: LatLng, b: LatLng): number {
  const y = Math.sin(toRad(b.lon - a.lon)) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(toRad(b.lon - a.lon));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// Punto y rumbo a `d` metros (en la escala de la API) del inicio del trayecto.
export function pointAt(g: RouteGeom, d: number): { ll: LatLng; bearing: number } | null {
  if (g.segs.length === 0) return null;
  let i = 0;
  while (i < g.segs.length - 1 && d > g.cum[i + 1]!) i++;
  const seg = g.segs[i]!;
  const span = g.cum[i + 1]! - g.cum[i]!;
  const frac = span > 0 ? Math.max(0, Math.min(1, (d - g.cum[i]!) / span)) : 1;
  const target = frac * seg.len;
  let k = 1;
  while (k < seg.pts.length - 1 && seg.acc[k]! < target) k++;
  const p = seg.pts[k - 1]!;
  const q = seg.pts[k]!;
  const segLen = seg.acc[k]! - seg.acc[k - 1]!;
  const f = segLen > 0 ? (target - seg.acc[k - 1]!) / segLen : 0;
  const ll = { lat: p.lat + (q.lat - p.lat) * f, lon: p.lon + (q.lon - p.lon) * f };
  return { ll, bearing: bearing(p, q) };
}

// Trazado oficial por calles: el tramo i va del vértice de la parada i al de la parada i+1.
export function fromRoutePath(
  path: readonly LatLng[],
  stopVertex: readonly number[],
  dists: readonly number[],
): RouteGeom {
  const segs: RouteGeom["segs"] = [];
  for (let i = 0; i + 1 < stopVertex.length; i++) {
    const pts = path.slice(stopVertex[i], stopVertex[i + 1]! + 1);
    segs.push(withAcc(pts.length >= 2 ? pts : [path[stopVertex[i]!]!, path[stopVertex[i + 1]!]!]));
  }
  return { cum: dists.slice(), segs };
}
